import { FC } from 'react'
import { Skeleton, SkeletonConversationList, SkeletonMessage } from '../ui'

/**
 * AppShellSkeleton — content-free loading frame for the authenticated app shell.
 *
 * Rendered by AuthGuard while boot-time auth is still resolving (isChecking),
 * replacing the old full-screen spinner. It paints ONLY the structural frame —
 * sidebar rails and chat-area placeholders as skeleton bars — so a returning
 * user perceives the app appearing instantly instead of staring at a spinner.
 *
 * CRITICAL (sec-redteam C1): this is presentational and has ZERO data
 * dependencies. It must NEVER render a real name, conversation title, or any
 * message body — nothing that hasn't been validated by the server. It shows
 * unconditionally during the check, including for a logged-out user who briefly
 * sees this frame before being redirected to /auth; because it contains no real
 * content, that leaks nothing.
 */
export const AppShellSkeleton: FC = () => (
  <div
    className="flex h-screen bg-sand-100 dark:bg-[#424242]"
    role="status"
    aria-label="Loading Arbinex…"
  >
    {/* Sidebar frame — width/palette intentionally mirror the real Sidebar
        (see Sidebar.tsx `<aside>`: w-64, bg-sand-200 dark:#2A2A2A, border-sand-300
        dark:#3A3A3A). Keep in sync if those change. Hidden on mobile where the
        real sidebar sits off-canvas. */}
    <aside className="hidden lg:flex flex-col w-64 bg-sand-200 dark:bg-[#2A2A2A] border-r border-sand-300 dark:border-[#3A3A3A]">
      {/* Header: logo placeholder */}
      <div className="p-4 border-b border-sand-300 dark:border-[#3A3A3A]">
        <Skeleton variant="text" className="h-6 w-28" />
      </div>

      {/* New-chat button placeholder */}
      <div className="p-3">
        <Skeleton variant="rectangular" className="h-10 w-full" />
      </div>

      {/* Conversation list rails */}
      <div className="flex-1 overflow-hidden py-2">
        <SkeletonConversationList count={6} />
      </div>

      {/* User-menu footer placeholder */}
      <div className="p-3 border-t border-sand-300 dark:border-[#3A3A3A]">
        <div className="flex items-center gap-3">
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" className="h-3 flex-1" />
        </div>
      </div>
    </aside>

    {/* Main content frame */}
    <div className="flex-1 flex flex-col min-w-0">
      <main className="flex-1 overflow-hidden">
        <div className="mx-auto max-w-3xl w-full px-4 py-8 space-y-6">
          <SkeletonMessage isUser />
          <SkeletonMessage />
          <SkeletonMessage isUser />
        </div>
      </main>

      {/* Composer placeholder */}
      <div className="px-4 pb-6">
        <div className="mx-auto max-w-3xl w-full">
          <Skeleton variant="rectangular" className="h-14 w-full" />
        </div>
      </div>
    </div>
  </div>
)
